import { getOperationToday } from 'shared/lib/dateFormatter';
import { isTodoDisplayed } from 'shared/lib/todoVisibility';

const priority = ['il_air_import', 'il_ftl_ltl', 'il_air_export', 'il_ocean_export', 'il_ocean_import'];

const todoFilterHandler = (todoData, selectedDate, type) => {
    const sortedTodos = [...todoData].sort((a, b) => {
        const aIndex = priority.findIndex((key) => a.tablename?.includes(key));
        const bIndex = priority.findIndex((key) => b.tablename?.includes(key));

        return aIndex - bIndex;
    });

    // Helper function to parse date string as local date (YYYY-MM-DD format)
    const parseLocalDate = (dateString) => {
        if (!dateString) return null;
        const dateMatch = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!dateMatch) {
            // Fall back to Date parsing if not in YYYY-MM-DD format
            const date = new Date(dateString);
            date.setHours(0, 0, 0, 0);
            return date;
        }
        const [, year, month, day] = dateMatch;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    // "오늘"은 브라우저가 아니라 운영 기준 시간대로 정한다 — 서버 조회와 같은 업무일을
    // 봐야 한국 등 다른 시간대에서 접속했을 때 서버가 보낸 카드가 화면에서 다시 걸러지지 않는다.
    const today = getOperationToday();

    // 날짜가 같은지 비교
    const isSameDate = (date1, date2) => date1.getTime() === date2.getTime();

    // 날짜가 같은 날이거나 과거인지 확인
    const isSameOrBefore = (date1, date2) => date1.getTime() <= date2.getTime();

    const filteredTodos =
        type === 'shipments'
            ? sortedTodos
            : sortedTodos.filter((item) => {
                  if (item.status === true || item.status == null || !item.intensity_date) return false;
                  if (!isTodoDisplayed(item)) return false;

                  // Parse normal_date as local date
                  const normalDate = parseLocalDate(item.normal_date);
                  if (!normalDate) return false;

                  if (isSameDate(selectedDate, today)) {
                      // 오늘일 경우, 오늘 또는 이전
                      return isSameOrBefore(normalDate, today);
                  } else {
                      // 오늘이 아닐 경우, 정확히 일치
                      return isSameDate(normalDate, selectedDate);
                  }
              });

    const filterByKey = (keys, step = null) => {
        return filteredTodos.filter((item) => {
            const isMatch = keys.includes(item.todo_key);

            // type이 shipments이면 step 필터링을 적용하지 않음
            // if (type === 'shipments') {
            //     return isMatch;
            // }

            // 그 외의 경우 step 필터링 적용
            return step !== null ? item.step === step && isMatch : isMatch;
        });
    };

    /**
     * A Case Closure card belongs to exactly one of Disputes or Shipment Closure (OS-575).
     * While disputing (attr_value === 'Disputing') it goes to Disputes, otherwise to Shipment Closure.
     * Both sections share the same isDisputing check, so once the dispute is cleared the card
     * simply moves back to Shipment Closure.
     */
    const isDisputing = (item) => item.todo_key === 'case_closure' && item.attr_value === 'Disputing';

    const filterDisputes = () => filteredTodos.filter(isDisputing);

    const filterClosure = () =>
        filteredTodos.filter(
            (item) => (item.todo_key === 'case_closure' && !isDisputing(item)) || item.todo_key === 'client_closure'
        );

    /**
     * 출발일·도착일·최종 도착 예정일 알림을 한 구획에 모은다.
     *
     * 출발일·도착일 카드는 값이 비어 있는 동안에는 입력 독촉(step 0)이고, 값이 들어오면
     * 그 날짜 당일 알림(step 1)으로 바뀐다. 최종 도착 예정일 카드는 담당자에게 입력을
     * 독촉하지 않아 단계 구분이 없고 항상 step 0으로 저장된다. 그래서 세 카드에 같은
     * 단계를 요구하면 최종 도착 확인 카드만 어느 구획에도 못 들어가 화면에서 사라진다.
     */
    const filterCargoSchedule = () => {
        return filteredTodos.filter((item) =>
            item.todo_key === 'feta' ? item.step === 0 : ['etd', 'eta'].includes(item.todo_key) && item.step === 1
        );
    };

    return {
        todoSections: {
            comment: filterByKey(['il_comment']),
            appointment: filterByKey([
                'pick_appointment',
                'prepull_appointment',
                'delivery_appointment',
                'move_appointment',
                'trans_load_status',
                'empty_status',
                'return_status',
                'gate_status',
                'load_status',
                'receive_status',
                'departure_status',
                'transshipment_status',
            ]),
            scheduling: filterByKey(['arranging_delivery', 'arranging_pickup', 'arranging_to']),
            data_input: filterByKey(['etd', 'eta', 'incoterm', 'intermodal', 'proof_link', 'pickup_number'], 0),
            customs_hold: filterByKey(['customs_hold']),
            cargo_schedule: filterCargoSchedule(),
            lfd: filterByKey(['demurrage']),
            export_documents: filterByKey(['dg_hazmat', 'shipping_instruction']),
            aes_itn_filing: filterByKey(['aes_itn']),
            stages: filterByKey(['stages']),
            release: filterByKey(['mbl_release', 'hbl_release']),
            booking_confirmation: filterByKey(['booking_confirmation']),
            prealert: filterByKey(['pre_alert']),
            air_freight: filterByKey(['air_freight']),
            ocean_freight: filterByKey(['ocean_freight']),
            pierpass: filterByKey(['pierpass']),
            import_service_fee: filterByKey(['import_service_fee']),
            isf_filing: filterByKey(['isf']),
            customs: filterByKey(['customs']),
            arrival_notice: filterByKey(['arrival_notice']),
            delivery_order: filterByKey(['delivery_order']),
            empty_container: filterByKey(['empty']),
            proof: filterByKey(['proof']),
            finance: filterByKey(['invoice', 'crdr']),
            disputes: filterDisputes(),
            closure: filterClosure(),
        },
    };
};

export { todoFilterHandler };
